import { createFileRoute, Link } from '@tanstack/react-router'
import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import {
  AdvisorsTabs,
  HeroStatusBand,
  IncidentsList,
  JobsTable,
  onSystemHealthRunAll,
  ObservabilityHub,
  ServiceGrid,
  systemHealthKeys,
  WebhookDeliveryCard,
} from '~/features/system-health'
import { useUrlState } from '~/lib/query/use-url-state'

/**
 * `/admin/system-health` — the System Health surface.
 *
 * URL search keys (incidents-only): `page`, `limit`.
 *
 * The incidents list is the only paginated surface on this page; the
 * other sub-components (hero band, service grid, advisors, jobs) are
 * count-bounded by their underlying queries today. When an incidents
 * API ships at `/api/v1/admin/system-health/incidents` the
 * `<IncidentsList>` component will read these URL keys directly via
 * `usePaginatedQuery`; until then we render the static placeholder
 * with a noop pagination footer to lock in the wiring.
 */
const systemHealthSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(25),
})
type SystemHealthSearch = z.infer<typeof systemHealthSearchSchema>

const URL_DEFAULTS: SystemHealthSearch = { page: 1, limit: 25 }

export const Route = createFileRoute('/admin/system-health')({
  validateSearch: systemHealthSearchSchema,
  component: SystemHealthRoute,
})

function SystemHealthRoute(): ReactElement {
  const search = Route.useSearch()
  const queryClient = useQueryClient()
  const [, setUrl] = useUrlState(systemHealthSearchSchema, URL_DEFAULTS)
  const [runningAll, setRunningAll] = useState(false)

  const runAllChecks = useCallback((): void => {
    setRunningAll(true)
    void queryClient
      .refetchQueries({ queryKey: systemHealthKeys.all, type: 'active' })
      .finally(() => setRunningAll(false))
  }, [queryClient])

  useEffect(() => onSystemHealthRunAll(runAllChecks), [runAllChecks])

  return (
    <div className="ka-page ka-system-health-page">
      <span className="ka-sr-only" role="status" aria-live="polite">
        {runningAll ? 'Running all system health checks' : ''}
      </span>
      <HeroStatusBand />
      <ServiceGrid />
      <ObservabilityHub />
      <AdvisorsTabs />
      <IncidentsList />
      <JobsTable />
      <WebhookDeliveryCard />
      <section className="ka-card" aria-label="User memory health">
        <header className="ka-card-head">
          <h2>User Memory Health</h2>
        </header>
        <p className="ka-card-body">
          Opt-in, extraction yield, wrong-memory rate, DLQ depth and cost per
          user for stored user memory.
        </p>
        <Link className="ka-btn ka-btn--ghost ka-btn--sm" to="/admin/memory-health">
          Open memory health
        </Link>
      </section>
      <section className="ka-card" aria-label="AI budget">
        <header className="ka-card-head">
          <h2>AI Budget</h2>
        </header>
        <p className="ka-card-body">
          Seats at or above 80% of their effective AI budget, overrides,
          top-ups, and organization PAYG.
        </p>
        <Link className="ka-btn ka-btn--ghost ka-btn--sm" to="/admin/ai-budget">
          Open AI budget
        </Link>
      </section>
      <div className="ka-sh-hidden-pagination" aria-hidden="true">
        <DataTablePagination
          page={search.page}
          limit={search.limit}
          total={0}
          isLoading={false}
          onChange={({ page, limit }) => setUrl({ page, limit })}
        />
      </div>
    </div>
  )
}
